import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { apiPost } from '../../lib/api'
import { AlertTriangle, Trash2, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react'

export default function DeleteAccountPage() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [confirmText, setConfirmText] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const isConfirmed = confirmText === 'DELETE'

  const deleteMutation = useMutation({
    mutationFn: (data: { password: string }) => apiPost('/auth/delete-account', data),
    onSuccess: async () => {
      try {
        await logout()
      } catch {
        // ignore logout errors
      }
      navigate('/login')
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to delete account')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!isConfirmed) {
      setError('Please type DELETE to confirm')
      return
    }
    if (!password) {
      setError('Please enter your password')
      return
    }

    const confirmed = window.confirm(
      'This action is permanent and cannot be undone. Are you absolutely sure you want to delete your account?'
    )
    if (!confirmed) return

    deleteMutation.mutate({ password })
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/profile" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <h1 className="text-xl font-bold text-gray-900">Delete Account</h1>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-red-800 mb-2">Danger Zone</h2>
            <p className="text-sm text-red-700 mb-3">
              Deleting your account is permanent and cannot be undone. The following will be permanently deleted:
            </p>
            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
              <li>Your profile and personal information</li>
              <li>All certifications and related data</li>
              <li>All uploaded documents and files</li>
              <li>Alert and notification settings</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type <span className="font-bold text-red-600">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="DELETE"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter your password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={!isConfirmed || !password || deleteMutation.isPending}
            className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 className="w-5 h-5" />
            {deleteMutation.isPending ? 'Deleting Account...' : 'Permanently Delete Account'}
          </button>
        </form>
      </div>
    </div>
  )
}
